import type { ImportCategoryCacheEntry, Transaction, TransactionPage, TransactionPayload, TransactionType } from "../types";
import { api } from "./core";

export type TransactionPageFilters = {
  type?: TransactionType | "";
  categoryId?: string;
  uncategorized?: boolean;
  userId?: string;
  accountId?: string;
  tagIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  linked?: boolean;
  logicalTransfers?: boolean;
  limit: number;
  offset: number;
};

function serializeTransactionPageFilters(filters: TransactionPageFilters) {
  const params = new URLSearchParams();
  if (filters.type) params.set("type", filters.type);
  if (filters.categoryId) params.set("category_id", filters.categoryId);
  if (filters.uncategorized) params.set("uncategorized", "true");
  if (filters.userId) params.set("user_id", filters.userId);
  if (filters.accountId) params.set("account_id", filters.accountId);
  filters.tagIds?.forEach((tagId) => params.append("tag_ids", tagId));
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.search) params.set("search", filters.search);
  if (filters.linked) params.set("linked", "true");
  if (filters.logicalTransfers) params.set("logical_transfers", "true");
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  return params.toString();
}

export const transactionsApi = {
  transactions: (groupId: string, filters: { type?: TransactionType | ""; categoryId?: string; userId?: string; accountId?: string }) => {
    const params = new URLSearchParams();
    if (filters.type) params.set("type", filters.type);
    if (filters.categoryId) params.set("category_id", filters.categoryId);
    if (filters.userId) params.set("user_id", filters.userId);
    if (filters.accountId) params.set("account_id", filters.accountId);
    const query = params.toString();
    return api<Transaction[]>(`/transactions${query ? `?${query}` : ""}`, { groupId });
  },
  transactionsPage: (
    groupId: string,
    filters: TransactionPageFilters,
  ) => api<TransactionPage>(`/transactions?${serializeTransactionPageFilters(filters)}`, { groupId }),
  transaction: (groupId: string, id: string) => api<Transaction>(`/transactions/${id}`, { groupId }),
  deletedTransactions: (groupId: string) => api<Transaction[]>("/transactions/deleted", { groupId }),
  deletedTransactionsPage: (
    groupId: string,
    filters: TransactionPageFilters,
  ) => api<TransactionPage>(`/transactions/deleted?${serializeTransactionPageFilters(filters)}`, { groupId }),
  createTransaction: (groupId: string, payload: TransactionPayload) =>
    api<Transaction>("/transactions", { method: "POST", body: JSON.stringify(payload), groupId }),
  updateTransaction: (groupId: string, id: string, payload: TransactionPayload) =>
    api<Transaction>(`/transactions/${id}`, { method: "PATCH", body: JSON.stringify(payload), groupId }),
  deleteTransaction: (groupId: string, id: string, options?: { restoreRecurringDueDate?: boolean }) => {
    const params = new URLSearchParams();
    if (options?.restoreRecurringDueDate) params.set("restore_recurring_due_date", "true");
    const query = params.toString();
    return api<void>(`/transactions/${id}${query ? `?${query}` : ""}`, { method: "DELETE", groupId });
  },
  restoreTransaction: (groupId: string, id: string) =>
    api<Transaction>(`/transactions/${id}/restore`, { method: "POST", groupId }),
  emptyDeletedTransactions: (groupId: string) =>
    api<void>("/transactions/deleted", { method: "DELETE", groupId }),
  debugDeleteAllTransactions: (groupId: string) =>
    api<void>("/debug/transactions", { method: "DELETE", groupId }),
  debugDeleteAllCategories: (groupId: string) =>
    api<void>("/debug/categories", { method: "DELETE", groupId }),
  debugDeleteAllAccounts: (groupId: string) =>
    api<void>("/debug/accounts", { method: "DELETE", groupId }),
  debugCategoryCache: (groupId: string) =>
    api<ImportCategoryCacheEntry[]>("/debug/category-cache", { groupId }),
};
